// app/routes/_landing.$slug.tsx
import type { LoaderFunctionArgs, LinksFunction } from 'react-router'
import type { Entry } from 'contentful'
import type { CfPageEntry, CfPageFields, CfEntry } from '~/types/contentful'

import { useLoaderData } from 'react-router'

import { getPageBySlug } from '~/services/contentful/pages.server'
import { contentPostToBlogPostSummary } from '~/services/contentful/blog.server'
import {
  cfSectionShellCssHref,
  renderSections,
  sectionCssMap,
} from '~/sections'

// Reuse the global meta util
export { meta } from '~/utils'

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const slug = params['*']
  if (!slug) throw new Response('Not found', { status: 404 })

  const entry: Entry<CfPageEntry, undefined, string> = await getPageBySlug(slug)
  if (!entry) throw new Response('Page not found', { status: 404 })

  const {
    title,
    slug: pageSlug,
    seoDescription,
    seoImage,
    isIndexed,
    sections = [],
  } = entry.fields as CfPageFields

  const safeSections = sections.filter((section): section is CfEntry =>
    Boolean(section?.sys?.contentType?.sys?.id),
  )

  // Transforms any cardGridSection sections into BlogPostSummary items and Card Items

  for (const [index, section] of safeSections.entries()) {
    if (section?.sys?.contentType?.sys?.id === 'cardGridSection') {
      if ((section.fields.cardCollection?.length ?? 0) > 0) {
        if (
          section.fields.cardCollection?.[0]?.sys?.contentType?.sys?.id ===
          'post'
        ) {
          safeSections[index].fields.collectionType = 'post'
          safeSections[index].fields.postCollection = safeSections[
            index
          ].fields.cardCollection
            .filter((post: any) => post?.sys?.contentType?.sys?.id === 'post')
            .map((post: any) => contentPostToBlogPostSummary(post))
        } else if (
          section.fields.cardCollection?.[0]?.sys?.contentType?.sys?.id ===
          'card'
        ) {
          safeSections[index].fields.collectionType = 'card'
          safeSections[index].fields.cardCollection = safeSections[
            index
          ].fields.cardCollection
            .filter((card: any) => Boolean(card?.fields))
            .map((card: any) => card.fields)
        }
      }
    }
  }

  // Derive which section types this page uses (for CSS loading)
  const sectionTypes: string[] = safeSections.map(
    (s: CfEntry) => s.sys?.contentType?.sys?.id,
  )

  // Normalize SEO image
  let seoImageSrc: string | undefined
  const imageFile = seoImage?.fields?.file
  if (imageFile?.url) {
    seoImageSrc = imageFile.url
  }

  const effectiveTitle = title
  const effectiveDescription = seoDescription ?? ''

  return {
    // 🔹 Top-level data that ~/utils.meta expects
    title: effectiveTitle,
    description: effectiveDescription,
    banner: seoImageSrc ? { src: seoImageSrc } : undefined,
    swiftypeSiteType: 'landing',
    noindex: !isIndexed, // default false → noindex

    // 🔹 For CSS loading
    sectionTypes,

    // 🔹 Page data for the component
    page: {
      id: entry.sys.id,
      title,
      slug: pageSlug,
      seoDescription,
      seoImageSrc: seoImageSrc ?? null,
      isIndexed: Boolean(isIndexed),
      sections: safeSections,
    },
  }
}

// Load only the CSS for sections that actually appear on this page

export const links: LinksFunction = (args?: { data?: any }) => {
  const links: { rel: 'stylesheet'; href: string }[] = [
    { rel: 'stylesheet', href: cfSectionShellCssHref },
  ]

  // In some Remix versions, `links` is called with no args (or without data)
  const data = (args as any)?.data as any | undefined

  // If we have loader data, use the sectionTypes from it; otherwise, fall back
  const sectionTypes: string[] =
    data?.sectionTypes && Array.isArray(data.sectionTypes)
      ? data.sectionTypes
      : Object.keys(sectionCssMap) // fallback: load all known section CSS

  const uniqueTypes = Array.from(new Set(sectionTypes))

  for (const typeId of uniqueTypes) {
    const hrefOrHrefs = sectionCssMap[typeId]
    const hrefs = Array.isArray(hrefOrHrefs) ? hrefOrHrefs : [hrefOrHrefs]

    for (const href of hrefs) {
      if (!href) continue
      links.push({ rel: 'stylesheet', href })
    }
  }

  return links
}

export default function LandingPageRoute() {
  const { page } = useLoaderData<typeof loader>()

  return <>{renderSections(page.sections as unknown as CfEntry[])}</>
}
